import { Badge, Box, Group, Loader, Paper, Progress, Stack, Text, TextInput, Title } from "@mantine/core";
import { IChecklistItemRow } from "@mctypes/checklist.types";
import { RowClassParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { AG_GRID_LOCALE_NOR } from "../../../../agGridLocale";
import { STANDARD_COLORS } from "../../../globalConstants";
import { useAppSelector } from "../../../../hooks";
import { ChecklistSummaryTable } from "../ChecklistSummaryTable";
import {
  selectActiveChecklistCheckKeysForPage,
  selectChecklist,
  selectChecklistHiddenColumns,
  selectChecklistItems,
  selectChecklistProgressSegmentsForPage,
  selectChecklistRowsForPage,
} from "../checklistSelectors";
import { DEFAULT_CHECKLIST_COL_DEF, getChecklistColumns } from "./checklistColumns";
import "./ChecklistTableRefactored.css";

const ChecklistBoardAgGridRefactored: React.FC = () => {
  const { checklistId } = useParams<{ checklistId: string }>();
  const checklist = useAppSelector((state) => selectChecklist(state, checklistId));
  const checklistItems = useAppSelector((state) => selectChecklistItems(state, checklistId));
  const rows = useAppSelector((state) => selectChecklistRowsForPage(state, checklistId));
  const activeCheckKeys = useAppSelector((state) => selectActiveChecklistCheckKeysForPage(state, checklistId));
  const { accepted, rejected } = useAppSelector((state) => selectChecklistProgressSegmentsForPage(state, checklistId));
  const hiddenColumns = useAppSelector(selectChecklistHiddenColumns);
  const [filterText, setFilterText] = useState("");
  const columns = getChecklistColumns(activeCheckKeys, checklist?.checkTypes, hiddenColumns);

  if (checklist == null) {
    return (
      <Group justify="center" flex={1} p="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="sm" h="100%" mih={0} p="xs">
      <Paper withBorder radius={0} p="sm">
        <Group justify="space-between" align="flex-start" gap="sm">
          <div>
            <Title order={5}>{checklist.name}</Title>
            <Text size="sm" c="dimmed">
              {checklist.id != null ? `#${checklist.id}` : checklist._id}
            </Text>
          </div>
          <Stack gap={4} w="min(420px, 45%)">
            <Group justify="space-between">
              <Badge variant="light">{checklist.status}</Badge>
              <Text size="xs" c="dimmed">
                {rows.length} rader
              </Text>
            </Group>
            <Progress.Root h={16} radius={4}>
              <Progress.Section value={accepted} color={STANDARD_COLORS.green} />
              <Progress.Section value={rejected} color={STANDARD_COLORS.red} />
            </Progress.Root>
          </Stack>
        </Group>
      </Paper>

      <Group justify="space-between" gap="sm">
        <Group gap="xs">
          {activeCheckKeys.map((checkKey) => (
            <Badge key={checkKey} variant="outline">
              {checklist.checkTypes?.[checkKey]?.name ?? checkKey}
            </Badge>
          ))}
        </Group>
        <TextInput
          size="sm"
          placeholder="Sok i sjekklisten"
          value={filterText}
          onChange={(event) => setFilterText(event.currentTarget.value)}
          w={220}
        />
      </Group>

      <Box className="ag-theme-quartz" style={{ flex: 1, minHeight: 360, width: "100%" }}>
        <AgGridReact<IChecklistItemRow>
          rowData={rows}
          columnDefs={columns}
          defaultColDef={DEFAULT_CHECKLIST_COL_DEF}
          getRowId={(params) => params.data._id}
          getRowClass={(params: RowClassParams<IChecklistItemRow>) =>
            params.data?.originalItem == null ? "checklist-refactored-category-row" : undefined
          }
          localeText={AG_GRID_LOCALE_NOR}
          quickFilterText={filterText}
          rowHeight={46}
          headerHeight={42}
          animateRows
          suppressCellFocus
          enableCellTextSelection
        />
      </Box>

      <ChecklistSummaryTable checklist={checklist} items={checklistItems} />
    </Stack>
  );
};

export default ChecklistBoardAgGridRefactored;
